import { useCurrency } from "@shared/selectors/globalSelectors";
import { Eye, X, Banknote, Info } from "lucide-react";
import React from "react";

interface Fee {
  id: string;
  name: string;
  description: string;
  amount: number;
  consumerCategory: string;
}

interface CategoryOption {
  value: string;
  label: string;
  name: string;
}

interface ApplicationProcessFeesListProps {
  fees: any;
  onRemoveFee: (id: string) => void;
  selectedCategory: string;
  categoryOptions: CategoryOption[];
}

const ApplicationProcessFeesList: React.FC<ApplicationProcessFeesListProps> = ({
  fees,
  onRemoveFee,
  selectedCategory,
  categoryOptions,
}) => {
  const currency = useCurrency();
  // Filter fees by selected category
  const filteredFees = fees.filter(
    (fee) => fee.consumerCategory === selectedCategory
  );

  const getCategoryLabel = (category: string) => {
    const categoryOption = categoryOptions.find(
      (option) => option.value === category
    );
    return categoryOption ? categoryOption.name : category;
  };

  const calculateTotal = () => {
    return filteredFees
      ?.flatMap((fee) => fee.fees)
      .reduce((sum, fee) => sum + fee.amount, 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <label className="font-semibold text-lg">Configured Fees</label>
        <Eye className="h-5 w-5 text-gray-500" />
      </div>
      <div className="relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current bg-card text-card-foreground mt-4">
        <Info className="h-4 w-4" />
        <div className="text-muted-foreground col-start-2 grid justify-items-start gap-1 [&_p]:leading-relaxed text-xs">
          <h6 className="font-semibold">Fee Configuration</h6>
          <p>
            These fees will be applied to all{" "}
            {getCategoryLabel(selectedCategory).toLowerCase()} connection
            applications. Users will see the total amount during the application
            process.
          </p>
        </div>
      </div>

      {filteredFees?.flatMap((fee) => fee.fees).length === 0 ? (
        <div className="border border-gray-200 rounded-lg p-8 bg-white text-center">
          <p className="text-gray-500">
            No fees configured for {getCategoryLabel(selectedCategory)}. Add
            fees using the form.
          </p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-6 bg-white">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-base font-normal">
              {getCategoryLabel(selectedCategory)} - Configured Fees
            </h2>
            <span className="font-semibold">
              Total: ${currency} {calculateTotal().toFixed(2)}
            </span>
          </div>

          <div className="space-y-4 max-h-64 overflow-y-auto">
            {filteredFees
              ?.flatMap((fee) => fee.fees)
              .map((fee) => (
                <div
                  key={fee?.id}
                  className="flex items-center justify-between p-4 bg-blue-50 border border-blue-100 rounded-lg"
                >
                  <div className="flex items-start gap-3 flex-1 overflow-y-auto">
                    <div className="text-blue-600 mt-1">
                      <Banknote className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">
                        {fee?.name}
                      </div>
                      <div className="text-sm text-gray-600 mt-0.5">
                        {fee?.description}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={fee?.amount?.toFixed(2)}
                      readOnly
                      className="w-24 px-3 py-2 text-right border border-gray-300 rounded bg-white"
                    />
                    <button
                      onClick={() => onRemoveFee(fee?.code)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                      aria-label="Remove fee"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationProcessFeesList;
